/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Form, Search, Icon, Message } from "semantic-ui-react";
import { observer } from "mobx-react";
import cx from "classnames";
import uuidv1 from "uuid/v1";
import Country from "~/models/Country";
import ExpandPanel from "~/components/element/ExpandPanel/ExpandPanel";
import AddressModel from "~/models/Address";
import GeoService from "~/services/GeoService";
import { narrowSuggestions } from "~/utils/narrowSuggestions";
import ValidateSchema, { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import "./Address.less";

// Use this schema along with utils/ValidateSchema
const addressValidation = {
  zip: {
    address: "zip",
  },
  country: {
    address: "country",
    required: true,
  },
  state: {
    address: "state",
  },
  city: {
    address: "city",
  },
  // No rules: the street line is optional (new construction, lots and unnamed roads have no
  // civic address). The key is kept so schema.fields.street stays defined for bindInput() and
  // for backend errors to bind to.
  street: {},
};

// External services and browser autofill use display names while the legacy
// Nest country model uses "USA" as its canonical value.
const COUNTRY_MODEL_ALIASES = {
  "etats-unis": "USA",
  "états-unis": "USA",
  "united states": "USA",
  "united states of america": "USA",
  "u.s.a.": "USA",
};

function normalizeCountryForModel(value) {
  const trimmedValue = String(value || "").trim();
  return COUNTRY_MODEL_ALIASES[trimmedValue.toLowerCase()] || trimmedValue;
}

function getAutocompleteCountries(modelCountry, preferredCountry) {
  const currentCountry = normalizeCountryForModel(modelCountry);
  const providerCountry = normalizeCountryForModel(preferredCountry);

  if (currentCountry) return [currentCountry];
  if (providerCountry) return [providerCountry];

  // Together these cover the overwhelming majority of new-address searches
  // when the current provider country is unavailable.
  return ["USA", "Canada"];
}

/**
 * Browser autofill can put a country display name in address line 2. Treat it
 * as autofill noise only when it resolves to the selected country; genuine
 * unit/suite values are returned unchanged.
 */
function sanitizeStreet2(street2, country) {
  const normalizedStreet2 = normalizeCountryForModel(street2).toLowerCase();
  const normalizedCountry = normalizeCountryForModel(country).toLowerCase();

  return normalizedCountry && normalizedStreet2 === normalizedCountry ? "" : street2;
}

/**
 * Extract address fields from a normalized HERE location object.
 * Returns the fields provided by HERE. Address line 2 is intentionally omitted
 * unless HERE actually resolved a unit, so a unit already entered by the user
 * survives autocomplete and reverse geocoding.
 */
function extractAddressFields(location) {
  const street = [location.street_number, location.route].filter(Boolean).join(" ");
  const fields = {
    inline: location.formatted_address || "",
    street: street || "",
    city: location.locality || "",
    country: normalizeCountryForModel(location.country),
    state: location.administrative_area_code || location.administrative_area || "",
    zip: location.postal_code || "",
  };

  if (location.unit) {
    fields.street2 = location.unit;
  }

  return fields;
}

function mergeAddressFields(model, location) {
  const fields = extractAddressFields(location);
  const street2 = Object.prototype.hasOwnProperty.call(fields, "street2") ? fields.street2 : model.street2 || "";

  return {
    ...model,
    ...fields,
    street2: sanitizeStreet2(street2, fields.country),
  };
}

@observer
class Address extends Component {
  formId = null;

  constructor(props) {
    super(props);

    this.onInlineAddressChange = this.onInlineAddressChange.bind(this);
    this.onDetailedAddressChange = this.onDetailedAddressChange.bind(this);
    // Generate a random hash of 4 chars long for unique html ids when component renders multiple times.
    this.formId = uuidv1()
      .replace(/-/g, "")
      .substring(0, 4);

    this.state = {
      showDetails: false,
      loadingResults: false,
      searchAllCountries: false,
    };

    this.autocompleteTimer = null;
    this.geocodeTimer = null;
    this.autocompleteRequestId = 0;
    this.geocodeRequestId = 0;
    this.detailsRequestId = 0;
    this.unmounted = false;
    // Last HERE answer, narrowed locally while the user keeps typing (see narrowSuggestions).
    this.lastAnswer = null;
  }

  componentDidUpdate(prevProps) {
    const { countries } = this.props;
    if (prevProps.countries.length === 0 && countries.length > 0) {
      this.forceUpdate();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.autocompleteTimer);
    clearTimeout(this.geocodeTimer);
  }

  onInlineAddressChange(value) {
    const {
      setModel,
      model,
      minSearchLength,
      setAddressResults,
      setSelectedAddressResult,
      preferredCountry,
    } = this.props;
    const { searchAllCountries } = this.state;

    clearTimeout(this.autocompleteTimer);
    clearTimeout(this.geocodeTimer);
    this.autocompleteRequestId += 1;
    this.geocodeRequestId += 1;
    this.detailsRequestId += 1;
    const requestId = this.autocompleteRequestId;

    setModel({ ...model, inline: value });
    setAddressResults([]);
    setSelectedAddressResult(null);

    if (!(value && value.length >= minSearchLength)) {
      this.setState({ loadingResults: false });
      return;
    }

    const countries = getAutocompleteCountries(model.country, preferredCountry);
    const opts = searchAllCountries ? {} : { countries };
    // Answers are only narrowed within the country scope they were fetched for.
    const scope = searchAllCountries ? "*" : countries.join(",");

    // Every call counts against the HERE rate limit: wait for a real pause in
    // typing, and longer when narrowed local results are already on screen.
    let delay = 400;
    const narrowed =
      this.lastAnswer && this.lastAnswer.scope === scope ? narrowSuggestions(this.lastAnswer, value) : null;
    if (narrowed) {
      this.setState({ loadingResults: false });
      setAddressResults(narrowed.suggestions);
      if (narrowed.exhaustive) return;
      delay = 1200;
    } else {
      this.setState({ loadingResults: true });
    }

    this.autocompleteTimer = setTimeout(() => {
      GeoService.autocomplete(value, opts)
        .then(suggestions => {
          if (this.unmounted || requestId !== this.autocompleteRequestId) return;

          if (suggestions.length > 0 || searchAllCountries) {
            this.lastAnswer = { scope, query: value, suggestions };
            this.setState({ loadingResults: false });
            setAddressResults(suggestions);
            return;
          }

          // A country preference should never hide a valid international
          // address. Expand automatically only after the preferred scope has
          // returned no suggestions.
          GeoService.autocomplete(value)
            .then(globalSuggestions => {
              if (this.unmounted || requestId !== this.autocompleteRequestId) return;

              this.lastAnswer = { scope: "*", query: value, suggestions: globalSuggestions };
              this.setState({ loadingResults: false, searchAllCountries: true });
              setAddressResults(globalSuggestions);
            })
            .catch(() => {
              if (this.unmounted || requestId !== this.autocompleteRequestId) return;

              this.setState({ loadingResults: false });
              setAddressResults([]);
            });
        })
        .catch(() => {
          if (this.unmounted || requestId !== this.autocompleteRequestId) return;

          this.setState({ loadingResults: false });
          setAddressResults([]);
        });
    }, delay);
  }

  onDetailedAddressChange(property, value) {
    const { setModel, model, setAddressResults, setSelectedAddressResult } = this.props;

    clearTimeout(this.autocompleteTimer);
    this.autocompleteRequestId += 1;

    if (property !== "street2") {
      this.detailsRequestId += 1;
    }

    const normalizedValue = property === "country" ? normalizeCountryForModel(value) : value;
    const sanitizedValue = property === "street2" ? sanitizeStreet2(normalizedValue, model.country) : normalizedValue;

    let newModel = setModel({ ...model, [property]: sanitizedValue });

    if (property === "country") {
      this.setState({ searchAllCountries: false });
      newModel = setModel({ ...newModel, state: "" });

      const sanitizedStreet2 = sanitizeStreet2(newModel.street2, sanitizedValue);
      if (sanitizedStreet2 !== newModel.street2) {
        newModel = setModel({ ...newModel, street2: sanitizedStreet2 });
      }
    }

    // Street lines (including units/suites in line 2) don't affect geolocation:
    // keep the current geocoded result untouched and don't call HERE.
    if (property === "street" || property === "street2") {
      return;
    }

    // City/state/postal code/country changes can move the property. Do not let
    // the form submit with coordinates from the previously selected address
    // while the replacement geocode request is pending.
    setAddressResults([]);
    setSelectedAddressResult(null);

    clearTimeout(this.geocodeTimer);
    this.geocodeRequestId += 1;
    const requestId = this.geocodeRequestId;

    // Only the postal code (or city for countries without one) is geolocated,
    // so we need at least one of them.
    if (!newModel.zip && !newModel.city) {
      this.setState({ loadingResults: false });
      setSelectedAddressResult(null);
      return;
    }

    const addressModel = new AddressModel(newModel);
    this.setState({ loadingResults: true });

    this.geocodeTimer = setTimeout(() => {
      GeoService.geocode(addressModel.getGeolocatableAddress())
        .then(location => {
          if (this.unmounted || requestId !== this.geocodeRequestId) return;

          this.setState({ loadingResults: false });
          if (location) {
            // Wrap in array using a suggestion-like shape so the search dropdown can show it
            const suggestion = { id: location.place_id, label: location.formatted_address };
            setAddressResults([suggestion]);
            setSelectedAddressResult(location);
          } else {
            setAddressResults([]);
            setSelectedAddressResult(null);
          }
        })
        .catch(() => {
          if (this.unmounted || requestId !== this.geocodeRequestId) return;

          this.setState({ loadingResults: false });
          setAddressResults([]);
        });
    }, 250);
  }

  onResultSelect(result) {
    const { setModel, setSelectedAddressResult, onLatLngChange } = this.props;

    clearTimeout(this.autocompleteTimer);
    clearTimeout(this.geocodeTimer);
    this.autocompleteRequestId += 1;
    this.geocodeRequestId += 1;
    this.detailsRequestId += 1;
    const requestId = this.detailsRequestId;

    if (!result.id) {
      throw new Error("Could not find selected result");
    }

    // HERE returns the parsed unit as structured suggestion data. Do not infer
    // one from the display label: "United States" starts with "Unit" and was
    // consequently being appended to the formatted address a second time.
    const unit = typeof result.unit === "string" && result.unit.trim() ? result.unit.trim() : undefined;

    this.setState({ loadingResults: true });

    GeoService.details(result.id, { unit })
      .then(location => {
        if (this.unmounted || requestId !== this.detailsRequestId) return;

        this.setState({ loadingResults: false });
        if (!location) return;

        const { model: currentModel } = this.props;
        const newModel = mergeAddressFields(currentModel, location);
        setModel(newModel);
        setSelectedAddressResult(location);

        if (onLatLngChange && location.latitude != null && location.longitude != null) {
          onLatLngChange({ lat: location.latitude, lng: location.longitude });
        }

        const inflightSchema = getValidationErrors(addressValidation, newModel, this.props);
        if (this.getMissingFields(inflightSchema).length > 0) {
          this.setState({ showDetails: true });
        }
      })
      .catch(() => {
        if (this.unmounted || requestId !== this.detailsRequestId) return;

        this.setState({ loadingResults: false });
      });
  }

  setSearchAllCountries(searchAllCountries) {
    const { model, minSearchLength } = this.props;

    this.setState({ searchAllCountries }, () => {
      if ((model.inline || "").length >= minSearchLength) {
        this.onInlineAddressChange(model.inline);
      }
    });
  }

  getHtmlIdFor(baseId) {
    return `${baseId}-${this.formId}`;
  }

  getMissingFields(schema) {
    const fields = ["street", "city", "state", "country", "zip"];
    const missingFields = [];

    fields.forEach(field => {
      if (schema.fields[field] && !schema.fields[field].isValid) {
        missingFields.push(field);
      }
    });

    return missingFields;
  }

  toggleDetails() {
    const { showDetails } = this.state;
    this.setState({ showDetails: !showDetails });
  }

  isFormValid() {
    const { schema } = this.props;

    return (
      schema.fields.street &&
      schema.fields.street.isValid &&
      schema.fields.city &&
      schema.fields.city.isValid &&
      schema.fields.state &&
      schema.fields.state.isValid &&
      schema.fields.country &&
      schema.fields.country.isValid &&
      schema.fields.zip &&
      schema.fields.zip.isValid
    );
  }

  render() {
    const {
      model,
      isDirty,
      schema,
      countries,
      hasSelectedResult,
      minSearchLength,
      addressResults,
      bindInput,
      t,
      useTranslation,
      serverValidationErrors,
      preferredCountry,
    } = this.props;
    const { showDetails, loadingResults, searchAllCountries } = this.state;
    let states = [];
    let selectedCountry = null;

    if (model.country) {
      selectedCountry = countries.find(countryInLoop => countryInLoop.id === model.country);

      if (selectedCountry) {
        states = selectedCountry.states;
      }
    }

    let postalCodeErrorMessage = "";

    if (selectedCountry && selectedCountry.zipCodeErrorMessage) {
      if (useTranslation) {
        postalCodeErrorMessage = t(`address:postalCodeErrorMessageFor${selectedCountry.title.replace(/ /g, "")}`);
      } else {
        postalCodeErrorMessage = selectedCountry.zipCodeErrorMessage;
      }
    }

    const hasServerErrors = Object.keys(serverValidationErrors).length > 0;
    const validAddress = this.isFormValid() && hasSelectedResult;
    const missingFields = this.getMissingFields(schema);
    const autocompleteCountries = getAutocompleteCountries(model.country, preferredCountry);
    const autocompleteCountryLabel = autocompleteCountries.join(" and ");
    const showSearchScope =
      !hasSelectedResult && typeof model.inline !== "undefined" && model.inline.length >= minSearchLength;

    return (
      <div className="address-wizard">
        <Form.Input
          fluid
          loading={loadingResults}
          showNoResults={
            !loadingResults && typeof model.inline !== "undefined" && model.inline.length >= minSearchLength
          }
          noResultsMessage={
            <span>
              No results found.&nbsp;
              <a href="#" onClick={() => this.toggleDetails()}>
                Enter manually
              </a>
            </span>
          }
          control={Search}
          id={this.getHtmlIdFor("tour-address-inline")}
          label={useTranslation ? t("clientPanelInfo:enterAnAddress") : "* Enter an address"}
          placeholder={useTranslation ? t("clientPanelInfo:enterFullPartialAddress") : "Enter full or partial address"}
          name="inline"
          autoComplete="off"
          value={model.inline || ""}
          results={addressResults.map(result => ({ title: result.label, id: result.id, unit: result.unit }))}
          onSearchChange={event => this.onInlineAddressChange(event.target.value)}
          onResultSelect={(_event, data) => this.onResultSelect(data.result)}
          disabled={showDetails}
        />
        {showSearchScope && (
          <div className="address-search-scope">
            {searchAllCountries ? "Searching all countries. " : `Searching first in ${autocompleteCountryLabel}. `}
            <a
              href="#"
              onClick={event => {
                event.preventDefault();
                this.setSearchAllCountries(!searchAllCountries);
              }}
            >
              {searchAllCountries ? `Prefer ${autocompleteCountryLabel}` : "Search all countries"}
            </a>
          </div>
        )}
        <Message
          size="small"
          warning={!validAddress}
          positive={validAddress}
          className={cx("address-status", {
            show: addressResults.length > 0 || ((model.inline || "").length > 0 && isDirty),
          })}
        >
          <Icon name={validAddress ? "check" : "warning"} />
          {validAddress ? "valid" : "invalid"} address.
          {missingFields.length > 0 ? ` Please complete the following fields: ${missingFields.join(", ")}.` : ""}
        </Message>
        <ExpandPanel
          className="details"
          open={showDetails || hasServerErrors}
          onClick={() => this.toggleDetails()}
          label={useTranslation ? t("clientPanelInfo:addressDetails") : "Address details"}
        >
          <Form.Input
            {...bindInput("street", schema, serverValidationErrors)}
            fluid
            label={useTranslation ? t("clientPanelInfo:addressLine1") : "Address Line 1"}
            id={this.getHtmlIdFor("street")}
            autoComplete={`section-${this.formId} address-line1`}
            placeholder={useTranslation ? t("clientPanelInfo:addressLine1") : "Address Line 1"}
            onChange={event => this.onDetailedAddressChange(event.target.name, event.target.value)}
          />
          <Form.Input
            {...bindInput("street2", schema, serverValidationErrors)}
            fluid
            id={this.getHtmlIdFor("street2")}
            autoComplete={`section-${this.formId} address-line2`}
            label={useTranslation ? t("clientPanelInfo:addressLine2") : "Address Line 2"}
            placeholder={useTranslation ? t("clientPanelInfo:addressLine2") : "Address Line 2"}
            onChange={event => this.onDetailedAddressChange(event.target.name, event.target.value)}
          />
          <Form.Input
            {...bindInput("city", schema, serverValidationErrors)}
            fluid
            id={this.getHtmlIdFor("city")}
            autoComplete={`section-${this.formId} address-level2`}
            label={useTranslation ? t("clientPanelInfo:asteriskCity") : "* City"}
            placeholder={useTranslation ? t("clientPanelInfo:city") : "City"}
            onChange={event => this.onDetailedAddressChange(event.target.name, event.target.value)}
          />
          <Form.Group widths="equal">
            <Form.Select
              {...bindInput("country", schema, serverValidationErrors)}
              fluid
              id={this.getHtmlIdFor("country")}
              label={useTranslation ? t("clientPanelInfo:asteriskCountry") : "* Country"}
              loading={countries.length === 0}
              placeholder={useTranslation ? t("clientPanelInfo:country") : "Country"}
              options={countries.map(countryInLoop => ({
                key: countryInLoop.id,
                text: countryInLoop.id,
                value: countryInLoop.id,
              }))}
              onChange={(_event, data) => this.onDetailedAddressChange("country", data.value)}
            />
            {!selectedCountry || selectedCountry.hasStates ? (
              <Form.Select
                {...bindInput("state", schema, serverValidationErrors)}
                fluid
                disabled={!selectedCountry}
                id={this.getHtmlIdFor("state")}
                label={useTranslation ? t("clientPanelInfo:asteriskState") : "* State"}
                placeholder={useTranslation ? t("clientPanelInfo:state") : "State"}
                options={Object.keys(states).map(key => ({ key, text: states[key], value: key }))}
                onChange={(_event, data) => this.onDetailedAddressChange("state", data.value)}
              />
            ) : null}
            <Form.Input
              {...bindInput("zip", schema, serverValidationErrors)}
              fluid
              id={this.getHtmlIdFor("zip")}
              autoComplete={`section-${this.formId} postal-code`}
              label={useTranslation ? t("clientPanelInfo:asteriskZip") : "* Zip"}
              placeholder={useTranslation ? t("clientPanelInfo:zip") : "Zip"}
              onChange={event => this.onDetailedAddressChange(event.target.name, event.target.value)}
            />
          </Form.Group>
          <div className="help">{postalCodeErrorMessage}</div>
        </ExpandPanel>
      </div>
    );
  }
}

const modelPropTypes = {
  inline: PropTypes.string,
  street: PropTypes.string,
  street2: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
};

const schemaPropTypes = {
  street: PropTypes.object,
  city: PropTypes.object,
  state: PropTypes.object,
  country: PropTypes.object,
  zip: PropTypes.object,
};

Address.propTypes = {
  model: PropTypes.shape({ ...modelPropTypes }).isRequired,
  schema: PropTypes.shape({
    isValid: PropTypes.bool,
    fields: PropTypes.shape({ ...schemaPropTypes }).isRequired,
  }).isRequired,
  isDirty: PropTypes.bool,
  setModel: PropTypes.func.isRequired,
  bindInput: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.instanceOf(Country)),
  minSearchLength: PropTypes.number,
  addressResults: PropTypes.array.isRequired,
  hasSelectedResult: PropTypes.bool,
  setAddressResults: PropTypes.func.isRequired,
  setSelectedAddressResult: PropTypes.func.isRequired,
  onLatLngChange: PropTypes.func,
  preferredCountry: PropTypes.string,
  t: PropTypes.func.isRequired,
  useTranslation: PropTypes.bool,
  serverValidationErrors: PropTypes.shape({
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    zip: PropTypes.string,
  }),
};

Address.defaultProps = {
  minSearchLength: 5,
  hasSelectedResult: false,
  useTranslation: false,
  serverValidationErrors: {},
  preferredCountry: "",
};

export default withTranslation()(Address);

const AddressWithValidation = withTranslation()(ValidateSchema({ ...addressValidation })(Address));

export {
  modelPropTypes,
  schemaPropTypes,
  addressValidation,
  AddressWithValidation,
  extractAddressFields,
  mergeAddressFields,
  normalizeCountryForModel,
  getAutocompleteCountries,
  sanitizeStreet2,
};
